import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
    scrollToTop,
    getAdjustedDate,
    getNumberOfDaysBetweenDates,
} from '../utilities/general-utils'
import validateExceptionFields from '../validation/validateExceptionFields'
import FormFieldInputCheckbox from '../components/ui/FormFieldInputCheckbox'
import {
    svc_getTransaction,
    svc_getException,
    svc_createException,
    svc_deleteException,
} from "../services/transaction.service";
import { normalizeRawException, normalizeExceptionFormData } from '../utilities/data-normalization'
import { dateHandler } from "../utilities/date-utils";
import { IconAngleLeftBold } from "../assets/icons";
import { useResponseMessages } from "../hooks/useResponseMessage";

const TransactionException = () => {
    const { getAccessTokenSilently, user } = useAuth0();
    const navigate = useNavigate();
    let {
        id: transactionIdParam,
        instance: instanceParam
    } = useParams()
    const url = new URL(window.location.href)
    const exceptionIdParam = url.searchParams.get('exceptionId')
    const { addMessage } = useResponseMessages();

    const [transaction, setTransaction] = useState(null)
    const [exception, setException] = useState(null)
    const [updatedException, setUpdatedException] = useState(null)
    const [hasChanged, setHasChanged] = useState(false)
    const [rescheduledDate, setRescheduledDate] = useState()
    const [createOrUpdate, setCreateOrUpdate] = useState('Create')
    const [hasExistingException, setHasExistingException] = useState(exceptionIdParam && exceptionIdParam.length > 0)

    const handleRescheduledDate = (days) => {
        setRescheduledDate(dateHandler.toSubmitFormat(
            getAdjustedDate(
                instanceParam,
                days
            )
        ))
    }

    const handleFormChange = (e) => {
        setHasChanged(true)
    }

    const getOriginalTransaction = async () => {
        const accessToken = await getAccessTokenSilently();
        const { data } = await svc_getTransaction(accessToken, user.sub, transactionIdParam);
        const result = await data[0]

        setTransaction({
            id: result.id,
            title: result.title,
            amount: parseFloat(result.amount).toFixed(2),
            instanceDate : instanceParam,
            startDate: result.start_date,
            endDate: result.end_date,
        })
    }

    const getTransactionException = async (exceptionId) => {
        if (!exceptionId) {
            const exceptionObj = {
                id: null,
                transactionId: transactionIdParam,
                isRescheduled: 0,
                isCancelled: 0,
                startDate: dateHandler.toZeroHoursFormat(instanceParam),
                endDate: dateHandler.toZeroHoursFormat(instanceParam),
                amount: null,
                instanceDate: instanceParam,
                daysAdjusted: 0,
                isPaid: 0,
                highlight: 1,
            }
            setException({
                ...exceptionObj
            })
            setUpdatedException({
                ...exceptionObj
            })

            return
        }

        const accessToken = await getAccessTokenSilently();
        const { data } = await svc_getException(accessToken, user.sub, exceptionId);

        if (data[0]) {
            const result = data[0]

            const exceptionObj = normalizeRawException({
                ...result,
                instanceDate: instanceParam
            })

            setException(exceptionObj)
            setUpdatedException(exceptionObj)
            setHasExistingException(true)

            if (exceptionObj.isRescheduled) {
                handleRescheduledDate(exceptionObj.daysAdjusted)
            }
        } else {
            navigate(`/exception/${transactionIdParam}/${instanceParam}`)
        }
    }

    const createException = async (e) => {
        e.preventDefault()

        const validation = validateExceptionFields(instanceParam, updatedException.endDate)

        if (validation.valid) {
            const accessToken = await getAccessTokenSilently();

            const exceptionData = normalizeExceptionFormData({
                ...updatedException,
                transactionId: transactionIdParam,
                instanceDate: instanceParam,
                startDate: instanceParam,
                amount: updatedException.amount ? updatedException.amount : transaction.amount,
            })

            const { data, error } = await svc_createException(accessToken, user.sub, exceptionData);

            if (error) {
                addMessage({
                    error: error.message
                })
            } else {
                getTransactionException(data.insertId)
                    .then(() => {
                        navigate(`/exception/${transactionIdParam}/${instanceParam}?exceptionId=${data.insertId}`)
                    })

                addMessage({
                    data: `Exception successfully ${createOrUpdate.toLowerCase()}d`
                })
            }
        }
    }

    const deleteException = async (e) => {
        e.preventDefault()

        const accessToken = await getAccessTokenSilently();
        const exceptionData = {
            exceptionId: exception.id,
            transactionId: transactionIdParam,
            instanceDate: instanceParam,
        }

        const { error } = await svc_deleteException(accessToken, user.sub, exception.id, exceptionData);

        if (error) {
            addMessage({
                error: error.message
            })
        } else {
            setCreateOrUpdate('Create')
            addMessage({
                data: `Exception successfully deleted`
            })
        }

        navigate(`/exception/${transactionIdParam}/${instanceParam}`)
        scrollToTop()
    }

    useEffect(() => {
        getTransactionException(exceptionIdParam).then(() => {
            getOriginalTransaction()
        })
    }, [exceptionIdParam])

    useEffect(() => {
        setCreateOrUpdate(hasExistingException ? 'Update' : 'Create')
    }, [hasExistingException])

    return transaction && exception ? (
        <div className="content-width-container">
            <Link to="/exceptions" className="button button-slim button-blue spc-m-b-1 spc-m-t-1">
                <IconAngleLeftBold fill="#fff" /> Back to My Exceptions
            </Link>
            <div className="standardFormWrap">
                <h2 className="standardFormHeading">{createOrUpdate} Exception</h2>

                {exceptionIdParam && (
                    <h4>
                        Exception ID: {exceptionIdParam}
                    </h4>
                )}

                <form
                    className="formStandard"
                    onChange={handleFormChange}
                    onSubmit={createException}
                >
                    <div className="responsive-table-wrap">
                        <table className="table-default table-cells-center table-3-col spc-m-x-auto">
                            <thead>
                                <tr>
                                    <th>&nbsp;</th>
                                    <th>Current</th>
                                    <th>New</th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr>
                                    <th>Title</th>
                                    <td colSpan="2" style={{ textAlign: 'center' }}>
                                        <Link to={`/transaction/${transactionIdParam}`}>{transaction.title}</Link>
                                    </td>
                                </tr>
                                <tr>
                                    <th>
                                        Instance Date{' '}
                                        {exception.isRescheduled
                                            ? '(original)'
                                            : ''}
                                    </th>
                                    <td colSpan="2" style={{ textAlign: 'center' }}>
                                        {dateHandler.toDisplayFormat(instanceParam)}
                                    </td>
                                </tr>
                                {updatedException.isRescheduled ? (
                                    <tr>
                                        <th>Date (rescheduled)</th>
                                        <td colSpan="2" style={{ textAlign: 'center' }}>
                                            {rescheduledDate && dateHandler.toDisplayFormat(rescheduledDate)}
                                        </td>
                                    </tr>
                                ) : (
                                    false
                                )}
                                <tr>
                                    <th>
                                        End Date{' '}
                                        <span style={{ display: 'block', fontSize: '75%', fontWeight: '300', lineHeight: '1.2'}}>
                                            Changes will be in effect for all instances through this date
                                        </span>
                                    </th>

                                    <td>
                                        {dateHandler.toDisplayFormat(
                                            (hasExistingException && exception && exception.endDate)
                                                ? exception.endDate
                                                : instanceParam
                                            )
                                        }
                                    </td>

                                    <td>
                                        <div className="formField">
                                            <input
                                                defaultValue={dateHandler.toInputFormat(updatedException.endDate || instanceParam)}
                                                type="date"
                                                required
                                                onChange={(e) => {
                                                    setUpdatedException({
                                                        ...updatedException,
                                                        endDate: dateHandler.toSubmitFormat(e.target.value)
                                                    })
                                                }}
                                            />
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th>Amount</th>
                                    <td>
                                        {hasExistingException
                                            ? parseFloat(exception.amount)
                                            : transaction.amount}
                                    </td>
                                    <td>
                                        <div className="formField">
                                            <input
                                                type="number"
                                                defaultValue={
                                                    hasExistingException
                                                        ? parseFloat(updatedException.amount)
                                                        : transaction.amount
                                                }
                                                step="any"
                                                onChange={(e) =>
                                                    setUpdatedException({
                                                        ...updatedException,
                                                        amount: parseFloat(
                                                            e.target.value
                                                        )
                                                    })
                                                }
                                            />
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th>Rescheduled</th>
                                    <td>
                                        {exception.isRescheduled ? 'Yes' : 'No'}
                                    </td>
                                    <td>
                                        <FormFieldInputCheckbox
                                            defaultChecked={updatedException.isRescheduled}
                                            onClick={(e) =>
                                                setUpdatedException({
                                                    ...updatedException,
                                                    isRescheduled: e.target.checked ? 1 : 0
                                                })
                                            }
                                        />
                                        {updatedException.isRescheduled ? (
                                            <div className="formField">
                                                <input
                                                    type="date" 
                                                    defaultValue={rescheduledDate}
                                                    onChange={(e) => {
                                                        const daysAdjusted = getNumberOfDaysBetweenDates(
                                                            new Date(instanceParam),
                                                            new Date(e.target.value)
                                                        )

                                                        handleRescheduledDate(daysAdjusted)

                                                        setUpdatedException({
                                                            ...updatedException,
                                                            daysAdjusted: daysAdjusted
                                                        })
                                                    }}
                                                />
                                            </div>
                                        ) : null}
                                    </td>
                                </tr>
                                <tr>
                                    <th>Cancelled</th>
                                    <td>
                                        {exception.isCancelled ? 'Yes' : 'No'}
                                    </td>
                                    <td>
                                        <FormFieldInputCheckbox
                                            value="1"
                                            defaultChecked={updatedException.isCancelled}
                                            onClick={(e) =>
                                                setUpdatedException({
                                                    ...updatedException,
                                                    isCancelled: e.target.checked ? 1 : 0
                                                })
                                            }
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>Paid</th>
                                    <td>
                                        {exception.isPaid ? 'Yes' : 'No'}
                                    </td>
                                    <td>
                                        <FormFieldInputCheckbox
                                            value="1"
                                            defaultChecked={updatedException.isPaid}
                                            onClick={(e) =>
                                                setUpdatedException({
                                                    ...updatedException,
                                                    isPaid: e.target.checked ? 1 : 0
                                                })
                                            }
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>Highlight Item</th>
                                    <td>
                                        {exception.highlight ? 'Yes' : 'No'}
                                    </td>
                                    <td>
                                        <FormFieldInputCheckbox
                                            value="1"
                                            defaultChecked={updatedException.highlight}
                                            onClick={(e) =>
                                                setUpdatedException({
                                                    ...updatedException,
                                                    highlight: e.target.checked ? 1 : 0
                                                })
                                            }
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="formField button-row spc-m-t-1">
                        <button className="button button-green" type="submit" disabled={!hasChanged}>
                            {hasExistingException ? 'Update' : 'Create'} Exception
                        </button>
                        {hasExistingException && (
                            <button
                                className="button button-red"
                                onClick={deleteException}
                            >
                                Delete Exception
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    ) : null
}

export default TransactionException
